import * as classNames from 'classnames';
import { ContextService } from '../../../../services/context_service';
import { DataService } from '../../../../services/data_service';
import { filterMethodFulltext } from '../../../../core/forms/helpers/filter_method_fulltext';
import formStyle from '../../../../core/forms/style/WForm.less';
import { getFieldKey, setLocaleFieldKeys, toggleI18nFields } from './functions/LocaleFields';
import { IDs } from '../../../../helpers/IDs';
import { i18n } from '../../../../i18n/i18n';
import { i18nLang } from '../../../../i18n/i18nLang';
import { LanguageSelect } from './LanguageSelect';
import { lazyInject } from '../../../../services/inversify';
import parse from 'html-react-parser';
import * as React from 'react';
import style from './MehrsprachigkeitFaq.less';
import { StructureService } from '../../../../services/structure_service';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { WFormsTableWrapper } from '../../../../core/table/WFormsTableWrapper';

export interface MehrsprachigkeitGlossarProps {
    admin?: boolean;
    data: any;
    isDialog?: boolean;
    isMultilingual?: boolean;
    isPublic?: boolean;
}

export interface MehrsprachigkeitFaqState {
    bereiche: any[];
    columnWidth: number;
    data: any[];
    language: string;
    locale: string;
    tabIndex: number;
}

export class MehrsprachigkeitFaq extends React.Component<MehrsprachigkeitGlossarProps, MehrsprachigkeitFaqState> {

    constructor(props: any) {
        super(props);
        const languageSelected: string = (props.data || {}).LANGUAGE_SELECTED || i18nLang.getUILang();
        this.state = {
            bereiche: [],
            columnWidth: null,
            data: null,
            language: languageSelected,
            locale: languageSelected,
            tabIndex: this.contextService.get('tabIdxMehrsprachigkeitFaq') ? this.contextService.get('tabIdxMehrsprachigkeitFaq') : 0
        };
        this.getCustomFormConfigFunc = this.getCustomFormConfigFunc.bind(this);
        this.init = this.init.bind(this);
        this.onResize = this.onResize.bind(this);
        this.wrapper = React.createRef();
    }

    @lazyInject(ContextService) private contextService: ContextService;
    @lazyInject(DataService) private dataService: DataService;

    private customColumns: string[] = ['frage', 'antwort'];
    private fieldKeys: any[] = [
        { name: 'frage' },
        { name: 'antwort' }
    ];
    private fields: any[] = [];
    private hiddenColumns: string[] = [];
    private isDialogReady: boolean = false;
    private showingColumns: string[] = [];
    private wrapper: any;

    componentDidMount(): void {
        window.addEventListener('resize', this.onResize);
        this.init();
    }

    componentDidUpdate(prevProps: any, prevState: any): void {
        const languageSelected: string = (this.props.data || {}).LANGUAGE_SELECTED || i18nLang.getUILang();

        if (languageSelected !== this.state.locale) {
            this.setState({ data: null, language: languageSelected, locale: languageSelected }, this.init);
        }
        this.onResize();
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.onResize);
    }

    getCustomFormConfigFunc(formConfig: any, formData: any): void {
        toggleI18nFields(formConfig.items, (formData || {}).values, this.state.language);
    }

    getCustomColumns(): any[] {
        const antwort: string = i18n((this.fields.find((field: any) => field.key === this.fieldKeys[1].locale) || {}).name, 'entity');
        const frage: string = i18n((this.fields.find((field: any) => field.key === this.fieldKeys[0].locale) || {}).name, 'entity');
        const keyAnwort = (data: any): string => getFieldKey(data, this.fieldKeys[1]);
        const keyFrage = (data: any): string => getFieldKey(data, this.fieldKeys[0]);

        return [
            {
                accessor: 'frage_table',
                Header: frage,
                HeaderText: frage,
                id: 'frage_table',
                type: 'text',
                filterMethod: (filter: any, row: any) => filterMethodFulltext(filter, row._original, keyFrage(row._original))
            },
            {
                accessor: 'antwort_table',
                Header: antwort,
                HeaderText: antwort,
                id: 'antwort_table',
                type: 'text',
                filterMethod: (filter: any, row: any) => filterMethodFulltext(filter, row._original, keyAnwort(row._original))
            }
        ];
    }

    getExtraFormElements(): any {
        return {
            bereich:
                <div className={formStyle.fieldBox}>
                    <LanguageSelect
                        label={`${i18n('Sprache', 'sys')}:`}
                        onChange={(language: string) => this.setState({ language })}
                        value={this.state.language}
                    />
                </div>
        };
    }

    init(): any {
        setLocaleFieldKeys(this.fieldKeys, this.state.locale);
        const fields: any[] = (((((StructureService || {}).structures || {})[IDs.MOD_ETOOL_ADMIN] || {})[IDs.ENT_FAQ] || {}).ATTS || [])
            .map((field: any) => ({
                key: field.KEY, ...(field.NAME && { name: field.NAME }), ...(field.HIDDEN && { hidden: field.HIDDEN })
            }));
        toggleI18nFields(fields, null, this.state.language, this.customColumns, (data: any) => {
            data = data || {};
            this.fields = fields;
            this.hiddenColumns = data.hiddenColumns || [];
            this.showingColumns = data.showingColumns || [];
        });
        this.loadData();
    }

    loadData(): void {
        this.dataService.loadData(
            IDs.ENT_FAQ, {}, { resolve: true, bundle: true },
            (data: any) => this.processData(data),
            IDs.MOD_ETOOL_ADMIN
        );
    }

    onResize(): void {
        const element: any = (this.wrapper || {}).current;

        if (!element || !(element.children || []).length) {
            return;
        }
        const dialog: HTMLElement = this.props.isDialog && !this.isDialogReady ? this.parents(element, 'div.ui-dialog')[0] : null;
        const row: HTMLElement = element.children[0].querySelector('div.ReactTable div.rt-thead div.rt-tr');
        const tbody: HTMLElement = element.children[0].querySelector('div.ReactTable div.rt-tbody');
        let posLeft: number = null;
        let posRight: number = null;
        let { columnWidth } = this.state;

        if (tbody && row && (row.children || []).length > 1) {
            const tdAntwort: DOMRect = row.children[1].getBoundingClientRect();
            const tdFrage: DOMRect = row.children[0].getBoundingClientRect();
            posLeft = +tdFrage.left || null;
            posRight = +tdAntwort.right || null;

            if (posLeft && posRight) {
                columnWidth = (Math.abs(posRight) - Math.abs(posLeft)) * (1 / 3);
            } else {
                columnWidth = 400;
            }
        }

        if (dialog) {
            const dialogContent: HTMLElement = dialog.children[1] as HTMLDivElement;

            if (dialogContent) {
                const top: string = dialog.style.top || '0px';
                dialog.style.maxHeight = `calc(100% - ${top}`;
                dialog.style.overflow = 'hidden';
                dialog.style.boxSizing = 'border-box';
                dialogContent.style.minHeight = 'unset';
                dialog.querySelectorAll('div.ui-resizable-handle').forEach((div: HTMLElement) => div.remove());
                this.isDialogReady = true;
            }
        }

        if (columnWidth !== this.state.columnWidth) {
            element.style.visibility = 'visible';
            this.setState({ columnWidth });
        }
    }

    parents(element: any, selector: string): any[] {
        const parents: HTMLCollection[] = [];

        while ((element = element.parentNode) && element !== document) {
            if (!selector || element.matches(selector)) {
                parents.push(element);
            }
        }

        return parents;
    }

    processData(data: any): void {
        data = data || {};
        const bundle: any = {};
        const faq: any[] = (data[IDs.ENT_FAQ] || []).filter((item: any) => this.props.admin
            || (item[this.fieldKeys[0].locale] !== null && item[this.fieldKeys[1].locale] !== null));
        const temp: any = {};

        ((data.bundle || {}).bereich || []).forEach((bereich: any) => {
            bereich.name = i18n(bereich.name || '', 'tab');
            bundle[bereich.tid] = bereich;
        });

        faq.forEach((row: any) => {
            const keyAnwort: string = getFieldKey(row, this.fieldKeys[1]);
            const keyFrage: string = getFieldKey(row, this.fieldKeys[0]);
            const antwort = parse(row[keyAnwort]);
            const frage: string = row[keyFrage];
            row.antwort = typeof row[keyAnwort] === 'string' ? row[keyAnwort] : '';
            row.antwort_table = <div style={{ padding: 3 }}>{antwort}</div>;
            row.frage_table = (
                <div style={{ color: 'rgb(102, 146, 194)', padding: 3 }}>
                    {frage}
                </div>
            );

            if (bundle[row.bereich_orig] && !temp[row.bereich_orig]) {
                temp[row.bereich_orig] = bundle[row.bereich_orig];
            } else if (!bundle[row.bereich_orig] && !temp[0]) {
                temp[0] = { name: i18n('Allgemein', 'tab'), rfg: 0 };
            }
        });
        const bereiche: any[] = Object.values(temp)
            .map((item: any) => item)
            .sort((a: any, b: any) => +a.rfg - +b.rfg);
        const tabIndex: any = !this.props.admin || !bereiche[this.state.tabIndex] ? { tabIndex: 0 } : {};
        this.setState({ bereiche, data: faq, ...tabIndex }, () => setTimeout(this.onResize, 750));
    }

    render(): JSX.Element {
        if (this.state.data === null) {
            return null;
        }

        if (!this.state.data.length) {
            return <>{this.renderTable()}</>
        }

        return (
            <div ref={this.wrapper}>
                <Tabs
                    key={`faq-${this.state.locale}`}
                    className={classNames(
                        this.props.admin || this.props.isPublic ? 'tabs-admin-datenverwaltung' : 'tabs-informationen_hilfe',
                        this.props.isDialog ? style.tabs : null
                    )}
                    selectedIndex={this.state.tabIndex}
                    onSelect={(tabIndex, lastIndex, event) =>
                        this.setState({ tabIndex }, () => this.contextService.set('tabIdxMehrsprachigkeitFaq', this.state.tabIndex))
                    }
                >
                    <TabList>
                        {this.renderTabs()}
                    </TabList>
                    {this.renderTabPanels()}
                </Tabs>
            </div>
        );
    }

    renderTable(bereich?: number): JSX.Element {
        return (
            <WFormsTableWrapper
                entity={IDs.ENT_FAQ}
                data={(this.state.data || []).filter((item: any) => item.bereich_orig === bereich)}
                module={IDs.MOD_ETOOL_ADMIN}
                actions={this.props.admin ? ['add', 'edit', 'delete'] : []}
                customColumns={this.getCustomColumns()}
                hiddenColumns={['bereich', 'frage', 'antwort']}
                columnWidths={[this.state.columnWidth]}
                customClass='Custom-ETA'
                formLayout='flowV2'
                customFormConfigFunc={this.getCustomFormConfigFunc}
                customCssStyle={{
                    formDialogWrapper: { width: 950 }
                }}
                extraFormElementBefore={this.props.isMultilingual ? this.getExtraFormElements() : null}
                onAfterAction={(action: string) => {
                    if (action === 'saveDone' || action === 'delDone') {
                        this.loadData();
                    }
                }}
                hideExport
                tableFixedHeader
                tableShowPagination={false}
                hideFilterReset
                showTableHelp={false}
                tablePageSize={5}
            />
        );
    }

    renderTabPanels(): JSX.Element[] {
        return this.state.bereiche.map((bereich: any, index: number) =>
            <TabPanel key={`panel-${index}`}>
                <div style={!index && !this.state.columnWidth ? { visibility: 'hidden' } : {}}>
                    {this.renderTable(bereich.tid)}
                </div>
            </TabPanel>
        );
    }

    renderTabs(): JSX.Element[] {
        return this.state.bereiche.map((bereich: any, index: number) =>
            <Tab key={`tab-${index}`}>
                {bereich.name}
            </Tab>
        );
    }
}
